import React, { useState, useEffect } from 'react';
import { MapPin, Car, Users, Crown, GraduationCap } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import apiService from '../apiService';

const PLAN_DETAILS = {
  basic: { name: 'BASIQUE', description: 'Trajets fixes partagés', icon: MapPin, color: '#10b981', features: ['Trajets limités', 'Partagé', 'Économique'] },
  'private-basic': { name: 'PRIVÉ BASIQUE', description: 'Trajets fixes privatisés', icon: Car, color: '#FF7800', features: ['Trajets limités', 'Privatisé', 'Confortable'] },
  standard: { name: 'STANDARD', description: 'Tous trajets partagés', icon: Users, color: '#FFB900', features: ['Tous les trajets', 'Partagé', 'Liberté maximale'] },
  premium: { name: 'PREMIUM', description: 'Tous trajets privatisés', icon: Crown, color: '#FF7800', features: ['Tous les trajets', 'Privatisé', 'Intimité garantie'] },
  university: { name: 'UNIVERSITÉ', description: 'Campus - 05h30 à 19h00', icon: GraduationCap, color: '#06b6d4', features: ['Campus uniquement', 'Horaires fixes', 'Tarif étudiant'] },
  elite: { name: 'ÉLITE', description: 'Taxi privé illimité', icon: Crown, color: '#dc2626', features: ['Illimité', '07:00 - 19:00', 'Préférence VIP'] },
};

export default function PlansPage() {
  const { editingPrices, setEditingPrices, planPrices, setPlanPrices, errorMessage, setErrorMessage } = useAdmin();
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      setLoadingPrices(true);
      try {
        const response = await apiService.request('/admin/prices', { userType: 'admin' });
        if (response?.prices && response.prices.length > 0) {
          const pricesMap = {};
          response.prices.forEach(price => { pricesMap[price.plan_key] = { monthly: price.monthly, yearly: price.yearly }; });
          setPlanPrices(pricesMap);
        } else {
          setPlanPrices({ basic: { monthly: 45000, yearly: 486000 }, 'private-basic': { monthly: 100000, yearly: 1080000 }, standard: { monthly: 60000, yearly: 648000 }, premium: { monthly: 150000, yearly: 1620000 }, university: { monthly: 55000, yearly: 594000 }, elite: { monthly: 2000000, yearly: 21600000 } });
        }
      } catch (error) {
        console.error('Erreur chargement des prix:', error);
        setPlanPrices({ basic: { monthly: 45000, yearly: 486000 }, 'private-basic': { monthly: 100000, yearly: 1080000 }, standard: { monthly: 60000, yearly: 648000 }, premium: { monthly: 150000, yearly: 1620000 }, university: { monthly: 55000, yearly: 594000 }, elite: { monthly: 2000000, yearly: 21600000 } });
      } finally { setLoadingPrices(false); }
    };
    fetchPrices();
  }, [setPlanPrices]);

  const handleSavePrices = async () => {
    if (savingPrices) return;
    try {
      setSavingPrices(true);
      const response = await apiService.request('/admin/prices', { method: 'POST', userType: 'admin', body: JSON.stringify({ prices: planPrices }) });
      if (response?.success) {
        setErrorMessage('');
        setEditingPrices(false);
        const updatedResponse = await apiService.request('/admin/prices', { userType: 'admin' });
        if (updatedResponse?.prices) {
          const updatedPrices = {};
          updatedResponse.prices.forEach(price => { updatedPrices[price.plan_key] = { monthly: price.monthly, yearly: price.yearly }; });
          setPlanPrices(updatedPrices);
        }
        alert('✅ Tarifs mis à jour avec succès');
      } else {
        setErrorMessage('Erreur lors de la mise à jour des prix');
        alert('❌ Erreur: ' + (response?.error || 'Erreur serveur'));
      }
    } catch (error) {
      setErrorMessage('Erreur de connexion au serveur');
      alert('❌ Erreur: ' + error.message);
    } finally { setSavingPrices(false); }
  };

  const handlePriceChange = (planKey, value) => {
    const monthly = parseInt(value) || 0;
    const yearly = Math.round(monthly * 12 * 0.9); // -10% annuel
    setPlanPrices(prev => ({ ...prev, [planKey]: { monthly, yearly } }));
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Gestion des Plans d'Abonnement</h1>
        <div className="page-actions">
          {!editingPrices ? (
            <button className="btn-primary" onClick={() => setEditingPrices(true)} disabled={loadingPrices}>Modifier les prix</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => setEditingPrices(false)} disabled={savingPrices} style={{ background: '#DC2626', color: 'white' }}>Annuler</button>
              <button className="btn-primary" onClick={handleSavePrices} disabled={savingPrices}>{savingPrices ? '💾 Enregistrement...' : 'Enregistrer'}</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
        {Object.entries(PLAN_DETAILS).map(([planKey, plan]) => {
          const Icon = plan.icon;
          const prices = planPrices[planKey] || { monthly: 0, yearly: 0 };
          return (
            <div key={planKey} style={{ background: 'white', border: `3px solid ${plan.color}`, borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ background: `${plan.color}20`, color: plan.color, padding: '1rem', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={28} />
                </div>
                <div>
                  <h2 style={{ margin: 0, color: plan.color, fontWeight: '700', fontSize: '1.35rem' }}>{plan.name}</h2>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#5C636E' }}>{plan.description}</p>
                </div>
              </div>

              <div style={{ padding: '1.25rem', background: `${plan.color}08`, borderRadius: '8px', marginBottom: '1.5rem', borderLeft: `4px solid ${plan.color}` }}>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#414852', lineHeight: '1.6' }}>{plan.description}</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '700', color: '#5C636E', letterSpacing: '0.05em' }}>Tarification</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Prix mensuel — éditable */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>Prix Mensuel</label>
                    {editingPrices ? (
                      <input
                        type="number"
                        value={prices.monthly || ''}
                        onChange={(e) => handlePriceChange(planKey, e.target.value)}
                        style={{ width: '100%', padding: '0.6rem', border: `2px solid ${plan.color}`, borderRadius: '6px', fontSize: '0.95rem', fontWeight: '600', color: plan.color, boxSizing: 'border-box' }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '1.25rem', color: plan.color }}>{(prices.monthly || 0).toLocaleString()} FC</p>
                    )}
                  </div>
                  {/* Prix annuel — calculé automatiquement */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>
                      Prix Annuel {editingPrices && <span style={{ color: '#10b981', fontSize: '0.65rem', fontWeight: '700', marginLeft: '4px' }}>−10% auto</span>}
                    </label>
                    <p style={{ margin: 0, fontWeight: '700', fontSize: '1.25rem', color: editingPrices ? '#9AA0A8' : plan.color }}>
                      {(prices.yearly || 0).toLocaleString()} FC
                    </p>
                    {editingPrices && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.7rem', color: '#9AA0A8' }}>
                        = {(prices.monthly || 0).toLocaleString()} × 12 × 90%
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '700', color: '#5C636E', letterSpacing: '0.05em' }}>Caractéristiques</p>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} style={{ margin: '0.5rem 0', fontSize: '0.95rem', color: '#414852', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <span style={{ color: plan.color, fontWeight: '700' }}>✓</span><span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ background: '#F4F5F7', padding: '0.75rem 1rem', borderRadius: '6px', borderLeft: `3px solid ${plan.color}` }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#5C636E', textTransform: 'uppercase', fontWeight: '600' }}>ID: <span style={{ color: plan.color, fontFamily: 'monospace' }}>{planKey}</span></p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
